import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { posthog } from '../lib/posthog';
import { useAuth } from '../hooks/use-auth';
import { useBillingUsage, asBillingUsageRow } from '../hooks/use-billing-usage';

/**
 * SentryPostHogIdentity — observability-stack Phase 4 Session 4.3 (ADR 0016).
 *
 * Side-effect-only component (returns null). Mounted UNDER <AuthProvider> in App.tsx so it
 * can call useBillingUsage()/useAuth() (both call useAuth() internally — circular if run inside
 * the provider). Wires analytics + error-tracking identity at auth hydration with the PII
 * asymmetry: email → PostHog only; Sentry gets id only (research/phase-4.md § 11 #5; ADR 0004).
 */
export function SentryPostHogIdentity(): null {
  const { user } = useAuth();
  const { data, isLoading } = useBillingUsage();
  // PLAN tier = tier_display_name off the billing row (D1/B3 — research/phase-4.md § 4.F).
  // asBillingUsageRow returns null for the {admin,unmetered} envelope → tier undefined → admins
  // identify with NO subscription_tier key (correct: admins are unmetered, no plan).
  const tier = asBillingUsageRow(data)?.tier_display_name;

  // IMP-1: fire identify once per SETTLED (user.id, tier). useBillingUsage has staleTime 30_000
  // (+ any refetchInterval) — a refetch that returns the SAME tier_display_name must NOT re-fire
  // posthog.identify. Dedupe on a ref of the last-identified key; skip while the billing query
  // is still loading (so we don't identify with a transient undefined tier then re-fire on settle).
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) {
      // Signed out → reset both SDKs' identity (shared-browser hygiene; memory: per-user-localstorage-scoping).
      posthog.reset(); // no-op if PostHog never init'd (dev — PROD-gated in 4.2)
      Sentry.setUser(null);
      lastKeyRef.current = null;
      return;
    }
    // IMP-1 guard: don't identify until the tier query settles (avoids an identify with a
    // transient `undefined` tier that then re-fires when the row lands).
    if (isLoading) return;

    // Dedupe: only re-identify when the (id, tier) pair actually changed. A 30s-staleTime refetch
    // that returns the same tier_display_name produces the same key → no re-fire (IMP-1).
    const key = `${user.id}::${tier ?? ''}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    // PII ASYMMETRY (§11 #5; spec Design Notes; AC-gated): PostHog gets email + tier + created_at.
    posthog.identify(user.id, {
      email: user.email, // user.email IS on the Supabase User (§4.D) — email TO PostHog
      subscription_tier: tier, // D1/B3 — tier_display_name (PLAN), NOT useApprovalStatus().tier
      created_at: user.created_at, // GoTrue standard field on the Supabase User (§4.D)
    });
    // Sentry gets ID ONLY — NEVER email (§11 #5, ADR 0004 PII rule). A symmetric {id,email} = AC FAILURE.
    Sentry.setUser({ id: user.id });
  }, [user?.id, user?.email, user?.created_at, tier, isLoading]);

  return null;
}
